import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  Select,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useReducer, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../contexts/userContext";
import { useActivities } from "../../hooks/useActivities";
import {
  actionButton,
  glassCard,
  mutedText,
  pageContainer,
  sectionHeading,
} from "../../themes/design";
import type { ActivityListItem } from "../../types/activities";
import {
  formatDistance,
  formatDuration,
  formatPace,
  formatRelativeDate,
  formatSpeed,
  isPaceSport,
  sportIcon,
} from "../../utils/activity-format";

interface State {
  items: ActivityListItem[];
  nextCursor: string | null;
  nextCursorId: string | null;
  isLoading: boolean;
  hasError: boolean;
  totalCount: number | null;
  sportTypes: string[];
  sportType: string;
  search: string;
}

type Action =
  | { type: "FETCH_START" }
  | {
      type: "FETCH_SUCCESS";
      items: ActivityListItem[];
      nextCursor: string | null;
      nextCursorId: string | null;
      append: boolean;
    }
  | { type: "FETCH_ERROR" }
  | { type: "SET_COUNT"; count: number }
  | { type: "SET_SPORT_TYPES"; sportTypes: string[] }
  | { type: "RESET"; sportType?: string; search?: string };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, isLoading: true, hasError: false };
    case "FETCH_SUCCESS":
      return {
        ...state,
        items: action.append ? [...state.items, ...action.items] : action.items,
        nextCursor: action.nextCursor,
        nextCursorId: action.nextCursorId,
        isLoading: false,
        hasError: false,
      };
    case "FETCH_ERROR":
      return { ...state, isLoading: false, hasError: true };
    case "SET_COUNT":
      return { ...state, totalCount: action.count };
    case "SET_SPORT_TYPES": {
      const merged = new Set([...state.sportTypes, ...action.sportTypes]);
      return { ...state, sportTypes: [...merged].sort() };
    }
    case "RESET":
      return {
        ...state,
        items: [],
        nextCursor: null,
        nextCursorId: null,
        isLoading: true,
        hasError: false,
        sportType: action.sportType ?? state.sportType,
        search: action.search ?? state.search,
      };
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
}

const initialState: State = {
  items: [],
  nextCursor: null,
  nextCursorId: null,
  isLoading: true,
  hasError: false,
  totalCount: null,
  sportTypes: [],
  sportType: "",
  search: "",
};

const ActivitiesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  const { listActivities, getCount } = useActivities();

  const [state, dispatch] = useReducer(reducer, initialState);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Generation counter to discard stale responses
  const fetchGenRef = useRef(0);
  // Ref for current filters — avoids stale closures in debounce callbacks
  const filtersRef = useRef({ sportType: "", search: "" });

  const fetchActivities = useCallback(
    async (
      cursor?: string,
      cursorId?: string,
      filterSport?: string,
      filterSearch?: string,
      append = false,
    ) => {
      const gen = ++fetchGenRef.current;
      dispatch({ type: "FETCH_START" });
      try {
        const result = await listActivities({
          cursor,
          cursorId,
          sportType: filterSport || undefined,
          search: filterSearch || undefined,
        });

        // Discard stale response — a newer fetch was initiated
        if (gen !== fetchGenRef.current) return;

        dispatch({
          type: "FETCH_SUCCESS",
          items: result.items,
          nextCursor: result.nextCursor,
          nextCursorId: result.nextCursorId,
          append,
        });

        // Accumulate sport types from unfiltered fetches (initial + Load More)
        if (!filterSport && !filterSearch) {
          const types = result.items.map((a) => a.sport_type);
          dispatch({ type: "SET_SPORT_TYPES", sportTypes: types });
        }
      } catch (err) {
        // Discard stale error
        if (gen !== fetchGenRef.current) return;
        console.error("Failed to fetch activities:", err);
        dispatch({ type: "FETCH_ERROR" });
      }
    },
    [listActivities],
  );

  // Initial load
  useEffect(() => {
    if (!user) return;
    fetchActivities().catch(() => {});
    getCount()
      .then((r) => dispatch({ type: "SET_COUNT", count: r.count }))
      .catch(() => {});
  }, [user, fetchActivities, getCount]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Debounced search — reads from filtersRef to avoid stale closure
  const handleSearchChange = (value: string) => {
    filtersRef.current = { ...filtersRef.current, search: value };
    dispatch({ type: "RESET", search: value });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const current = filtersRef.current;
      fetchActivities(undefined, undefined, current.sportType, current.search).catch(() => {});
    }, 300);
  };

  const handleSportTypeChange = (value: string) => {
    filtersRef.current = { ...filtersRef.current, sportType: value };
    dispatch({ type: "RESET", sportType: value });
    fetchActivities(undefined, undefined, value, filtersRef.current.search).catch(() => {});
  };

  const handleLoadMore = () => {
    if (state.nextCursor) {
      fetchActivities(
        state.nextCursor,
        state.nextCursorId ?? undefined,
        state.sportType,
        state.search,
        true,
      ).catch(() => {});
    }
  };

  if (!user) return null;

  const hasActiveFilters = state.sportType !== "" || state.search !== "";

  return (
    <Box {...pageContainer} maxW="800px">
      {/* Header */}
      <HStack justifyContent="space-between" alignItems="baseline">
        <Heading {...sectionHeading}>{t("ActivitiesPage")}</Heading>
        {state.totalCount !== null && !hasActiveFilters && (
          <Text {...mutedText} fontSize="sm">
            {t("ActivityCount", { count: state.totalCount })}
          </Text>
        )}
      </HStack>

      {/* Filter bar */}
      <HStack gap={3}>
        <Input
          placeholder={t("SearchActivities")}
          value={state.search}
          onChange={(e) => handleSearchChange(e.target.value)}
          bg="whiteAlpha.50"
          borderColor="whiteAlpha.200"
          color="primary.200"
          _placeholder={{ color: "primary.500" }}
          size="sm"
        />
        <Select
          value={state.sportType}
          onChange={(e) => handleSportTypeChange(e.target.value)}
          bg="whiteAlpha.50"
          borderColor="whiteAlpha.200"
          color="primary.200"
          size="sm"
          maxW="180px"
        >
          <option value="">{t("AllSports")}</option>
          {state.sportTypes.map((st) => (
            <option key={st} value={st}>
              {st}
            </option>
          ))}
        </Select>
      </HStack>

      {/* Activity cards */}
      <VStack gap={3} w="100%">
        {state.isLoading && state.items.length === 0
          ? ["s1", "s2", "s3", "s4", "s5"].map((id) => (
              <Skeleton key={id} h="80px" w="100%" borderRadius="xl" />
            ))
          : state.items.map((activity) => (
              <Box
                key={activity.activity_id}
                {...glassCard}
                w="100%"
                cursor="pointer"
                transition="all 0.2s ease"
                _hover={{
                  borderColor: "cyan.400",
                  boxShadow: "0 0 20px rgba(0, 255, 255, 0.15)",
                }}
                onClick={() => navigate(`/activities/${activity.activity_id}`)}
              >
                <Flex justifyContent="space-between" alignItems="flex-start">
                  <HStack gap={3} alignItems="flex-start">
                    <Text fontSize="xl">{sportIcon(activity.sport_type)}</Text>
                    <VStack alignItems="flex-start" gap={0}>
                      <Text color="primary.100" fontWeight="600" fontSize="sm">
                        {activity.name}
                      </Text>
                      <Text {...mutedText} fontSize="xs">
                        {activity.activity_start_date
                          ? formatRelativeDate(activity.activity_start_date)
                          : "--"}
                      </Text>
                    </VStack>
                  </HStack>
                </Flex>
                <HStack gap={4} mt={2}>
                  <Text color="primary.200" fontSize="sm">
                    {formatDistance(activity.distance)}
                  </Text>
                  <Text {...mutedText} fontSize="sm">
                    {formatDuration(activity.moving_time)}
                  </Text>
                  <Text {...mutedText} fontSize="sm">
                    {isPaceSport(activity.sport_type)
                      ? formatPace(activity.distance, activity.moving_time)
                      : formatSpeed(activity.distance, activity.moving_time)}
                  </Text>
                </HStack>
              </Box>
            ))}

        {/* Error state */}
        {state.hasError && !state.isLoading && (
          <Text color="red.400" py={8}>
            {t("FetchError")}
          </Text>
        )}

        {/* Empty state */}
        {!state.isLoading && !state.hasError && state.items.length === 0 && (
          <Text {...mutedText} py={8}>
            {t("NoActivities")}
          </Text>
        )}

        {/* Load More */}
        {state.nextCursor && (
          <Button
            {...actionButton}
            onClick={handleLoadMore}
            isLoading={state.isLoading}
            size="sm"
            w="100%"
          >
            {t("LoadMore")}
          </Button>
        )}
      </VStack>
    </Box>
  );
};

export default ActivitiesPage;
